import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  ShoppingBag,
  Package,
  Mail,
  Info,
  Tag,
  Truck,
  Store,
  User,
  Lock,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';

export interface PttAVMCredentials {
  shopId: string;
  apiUsername: string;
  apiPassword: string;
}

interface PttAVMConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (credentials: PttAVMCredentials, connectionId: string) => void;
}

export function PttAVMConnectModal({
  isOpen,
  onClose,
  onSuccess,
}: PttAVMConnectModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const { user } = useAuth();

  const [shopId, setShopId] = useState('');
  const [apiUsername, setApiUsername] = useState('');
  const [apiPassword, setApiPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'validating' | 'success'>('form');
  const [supplierName, setSupplierName] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!shopId.trim()) {
      setError(isEnglish ? 'Shop ID is required' : 'Magaza ID zorunludur');
      return;
    }

    if (!apiUsername.trim()) {
      setError(isEnglish ? 'API Username is required' : 'API Kullanici Adi zorunludur');
      return;
    }

    if (!apiPassword.trim()) {
      setError(isEnglish ? 'API Password is required' : 'API Sifresi zorunludur');
      return;
    }

    if (!user?.uid) {
      setError(isEnglish ? 'Please login first' : 'Lutfen once giris yapin');
      return;
    }

    setIsValidating(true);
    setStep('validating');

    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

      // Save connection via Cloud Function (validates and saves to Firestore)
      const response = await fetch(`${FUNCTIONS_URL}/savePttavm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          shopId: shopId.trim(),
          apiUsername: apiUsername.trim(),
          apiPassword: apiPassword.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setStep('form');
        setError(result.error || (isEnglish ? 'Invalid credentials' : 'Gecersiz kimlik bilgileri'));
        setIsValidating(false);
        return;
      }

      // Success!
      const credentials: PttAVMCredentials = {
        shopId: shopId.trim(),
        apiUsername: apiUsername.trim(),
        apiPassword: apiPassword.trim(),
      };

      setSupplierName(result.supplierName || '');
      setStep('success');
      setTimeout(() => {
        onSuccess(credentials, result.connectionId);
        handleClose();
      }, 1500);
    } catch (err) {
      setStep('form');
      setError(
        err instanceof Error
          ? err.message
          : isEnglish
          ? 'Connection failed. Please try again.'
          : 'Baglanti basarisiz. Lutfen tekrar deneyin.'
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleClose = () => {
    setShopId('');
    setApiUsername('');
    setApiPassword('');
    setShowPassword(false);
    setError(null);
    setStep('form');
    setSupplierName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-surface rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-border-default">
        {/* Header with PttAVM Branding */}
        <div
          className="relative p-6 pb-8"
          style={{
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
          }}
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-gray-800 transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-4">
            {/* PttAVM Logo */}
            <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shadow-lg">
              <Mail size={32} className="text-[#FFD700]" />
            </div>
            <div className="text-gray-800">
              <h2 className="text-xl font-bold">PttAVM</h2>
              <p className="text-gray-700 text-sm">
                {isEnglish ? 'Connect your seller account' : 'Satici hesabinizi baglayin'}
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="flex flex-wrap gap-3 mt-6">
            {[
              { icon: ShoppingBag, label: isEnglish ? 'Orders' : 'Siparisler' },
              { icon: Package, label: isEnglish ? 'Products' : 'Urunler' },
              { icon: Tag, label: isEnglish ? 'Categories' : 'Kategoriler' },
              { icon: Truck, label: isEnglish ? 'Shipping' : 'Kargo' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-gray-700 text-sm">
                <Icon size={14} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'validating' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#FFD700]/10 flex items-center justify-center mx-auto mb-4">
                <Loader2 size={32} className="text-[#FFD700] animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {isEnglish ? 'Validating credentials...' : 'Kimlik bilgileri dogrulaniyor...'}
              </h3>
              <p className="text-sm text-text-secondary">
                {isEnglish
                  ? 'Connecting to PttAVM API'
                  : "PttAVM API'sine baglaniliyor"}
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {isEnglish ? 'Successfully connected!' : 'Basariyla baglandi!'}
              </h3>
              <p className="text-sm text-text-secondary">
                {supplierName
                  ? (isEnglish ? `Connected as ${supplierName}` : `${supplierName} olarak baglandi`)
                  : (isEnglish ? 'Your PttAVM account is now connected' : 'PttAVM hesabiniz artik bagli')}
              </p>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error Banner */}
              {error && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                  <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Info Banner */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FFD700]/10 border border-[#FFD700]/30">
                <Info size={20} className="text-[#FFA500] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-[#B8860B] font-medium mb-1">
                    {isEnglish ? 'Where to find your API credentials?' : 'API bilgilerinizi nereden bulabilirsiniz?'}
                  </p>
                  <p className="text-text-secondary">
                    {isEnglish
                      ? 'Go to PttAVM Seller Panel -> Integration Settings -> API Settings'
                      : 'PttAVM Satici Paneli -> Entegrasyon Ayarlari -> API Ayarlari'}
                  </p>
                </div>
              </div>

              {/* Shop ID */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  {isEnglish ? 'Shop ID' : 'Magaza ID'}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary">
                    <Store size={18} />
                  </div>
                  <input
                    type="text"
                    value={shopId}
                    onChange={(e) => setShopId(e.target.value)}
                    placeholder="518928"
                    className="w-full px-4 py-3 pl-11 rounded-xl bg-bg-elevated border border-border-default focus:border-[#FFD700] focus:outline-none focus:ring-2 focus:ring-[#FFD700]/20 text-text-primary placeholder:text-text-tertiary transition-all font-mono text-sm"
                  />
                </div>
              </div>

              {/* API Username */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  API {isEnglish ? 'Username' : 'Kullanici Adi'}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    value={apiUsername}
                    onChange={(e) => setApiUsername(e.target.value)}
                    placeholder={isEnglish ? 'Your API username' : 'API kullanici adiniz'}
                    className="w-full px-4 py-3 pl-11 rounded-xl bg-bg-elevated border border-border-default focus:border-[#FFD700] focus:outline-none focus:ring-2 focus:ring-[#FFD700]/20 text-text-primary placeholder:text-text-tertiary transition-all text-sm"
                  />
                </div>
              </div>

              {/* API Password */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  API {isEnglish ? 'Password' : 'Sifresi'}
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary">
                    <Lock size={18} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={apiPassword}
                    onChange={(e) => setApiPassword(e.target.value)}
                    placeholder="********"
                    className="w-full px-4 py-3 pl-11 pr-12 rounded-xl bg-bg-elevated border border-border-default focus:border-[#FFD700] focus:outline-none focus:ring-2 focus:ring-[#FFD700]/20 text-text-primary placeholder:text-text-tertiary transition-all font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 rounded-xl bg-bg-elevated border border-border-default text-text-secondary hover:bg-bg-surface hover:text-text-primary transition-all font-medium"
                >
                  {isEnglish ? 'Cancel' : 'Iptal'}
                </button>
                <button
                  type="submit"
                  disabled={isValidating}
                  className={cn(
                    'flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2',
                    'bg-[#FFD700] text-gray-800 hover:bg-[#FFA500]',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {isValidating ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {isEnglish ? 'Connecting...' : 'Baglaniyor...'}
                    </>
                  ) : (
                    isEnglish ? 'Connect' : 'Baglan'
                  )}
                </button>
              </div>

              {/* Help Link */}
              <div className="text-center pt-2">
                <a
                  href="https://developers.pttavm.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-text-tertiary hover:text-[#FFD700] transition-colors"
                >
                  <ExternalLink size={14} />
                  {isEnglish ? 'API Documentation' : 'API Dokumantasyonu'}
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
